import { resolveFishGrade } from "../components/fish-identity.js";
import { titleIncluded, projectTitleProgress } from "./title-progress.js";
function element(tag, className = "", text = "") {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
}
function content(node, value) {
  if (node.textContent !== value) node.textContent = value;
}

const GRADES = ["red", "yellow", "blue", "green", "white", "unknown", "other"];

export function groupFishingTitles(titles, entries, preferences) {
  const groups = new Map(GRADES.map(grade => [grade, []]));
  for (const title of titles) {
    if (!titleIncluded(title, preferences)) continue;
    const fish = entries.get(title.itemId);
    const grade = title.itemId === null ? "other" : resolveFishGrade({ ...fish, isPrize: fish?.is_prize });
    groups.get(grade).push(title);
  }
  for (const [grade, rows] of groups) {
    if (!rows.length) groups.delete(grade);
  }
  return groups;
}

// Derived ownership index: count edits visit only the affected fish, while the
// existing domain projection remains the sole owner of acquisition rules.
export function createTitleOwnershipIndex(groups, entries, guideCount) {
  const ownedIds = new Set();
  const byFish = new Map();
  const byId = new Map();
  for (const titles of groups.values()) for (const title of titles) {
    byId.set(title.titleId, title);
    if (title.kind === "catch" && title.itemId !== null) {
      const rows = byFish.get(title.itemId) || [];
      rows.push(title);
      byFish.set(title.itemId, rows);
    }
  }
  return {
    ownedIds,
    update(projection, changes) {
      const changedIds = new Set();
      function set(id, owned) {
        if (ownedIds.has(id) === owned) return;
        changedIds.add(id);
        if (owned) ownedIds.add(id); else ownedIds.delete(id);
      }
      for (const id of changes ? changes.countFishIds : byFish.keys()) {
        const titles = byFish.get(id);
        if (!titles) continue;
        const entry = entries.get(id);
        const owned = new Set(projectTitleProgress(entry || { itemId: id }, titles, projection,
          entry ? guideCount(entry) : { kind: "unknown", count: null }).ownedTitleIds);
        for (const title of titles) set(title.titleId, owned.has(title.titleId));
      }
      const explicit = new Set(projection.obtainedTitleIds || []);
      for (const id of changes ? changes.titleIds : byId.keys()) {
        const title = byId.get(id);
        if (title && (title.kind !== "catch" || title.itemId === null)) set(id, explicit.has(id));
      }
      return changedIds;
    },
  };
}

/** Derived title statistics; the caller owns subscriptions and persistence. */
export function createTitleDashboard({ root, text, t, guideCount }) {
  const host = root.querySelector("[data-title-groups]");
  const totals = root.querySelector("[data-title-totals]");
  let key = null;
  let index = null;
  let fishSource = null;
  let groups = new Map();
  let views = [];
  function clear() {
    views = [];
    host?.replaceChildren();
  }
  function stat(grade, titles, parent) {
    const overall = grade === "overall";
    const label = overall ? t("fishydex.progress.kicker") : grade === "other" ? text("other")
      : grade === "unknown" ? text("unknown") : t(`domain.grade.${grade}`);
    const card = element("div", `${overall ? "fishydex-progress-overview" : "fishydex-grade-progress-card"} rounded-box border border-base-300 bg-base-200`);
    card.dataset.titleGrade = grade;
    const ring = element("div", `radial-progress fishydex-progress-pie ${overall ? "is-overall" : `grade-${grade}`}`);
    ring.setAttribute("role", "progressbar");
    ring.setAttribute("aria-valuemin", "0");
    ring.setAttribute("aria-valuemax", "100");
    ring.style.setProperty("--size", overall ? "7.5rem" : "4.85rem");
    ring.style.setProperty("--thickness", overall ? "0.72rem" : "0.48rem");
    const percent = element("span", overall ? "fishydex-progress-pie-value" : "fishydex-grade-progress-percent");
    ring.append(percent);
    const copy = element("div", overall ? "fishydex-progress-overview-copy" : "fishydex-grade-progress-copy");
    copy.append(element("span", overall ? "fishydex-progress-kicker" : `fishydex-grade-progress-title fishy-domain-label grade-${grade}`, label));
    if (overall) copy.append(element("span", "fishydex-progress-title", text("completion")));
    const count = element("span", overall ? "fishydex-progress-count" : "fishydex-grade-progress-value");
    const remaining = element("span", overall ? "fishydex-subtle" : "fishydex-grade-progress-meta");
    copy.append(count, remaining);
    card.append(ring, copy);
    parent.append(card);
    views.push({ titles, ring, percent, count, remaining, label });
  }
  return {
    update({ catalog, entries, projection, changes, contextKey }) {
      if (!host) return;
      host.hidden = !catalog;
      const nextKey = JSON.stringify([contextKey, projection.showContestTitles, projection.showUnobtainableTitles]);
      const rebuild = key !== nextKey || fishSource !== entries;
      if (rebuild) {
        clear();
        key = nextKey;
        fishSource = entries;
        groups = groupFishingTitles(catalog?.titles || [], entries, projection);
        index = createTitleOwnershipIndex(groups, entries, guideCount);
        stat("overall", [...groups.values()].flat(), host);
        const grades = element("div", "fishydex-grade-stats");
        host.append(grades);
        for (const grade of GRADES) {
          const titles = groups.get(grade) || [];
          if ((grade === "unknown" || grade === "other") && !titles.length) continue;
          stat(grade, titles, grades);
        }
      }
      const changedIds = index.update(projection, rebuild ? null : changes);
      const all = rebuild || !changes;
      for (const view of views) {
        if (!all && !view.titles.some(title => changedIds.has(title.titleId))) continue;
        const total = view.titles.length;
        const owned = view.titles.filter(title => index.ownedIds.has(title.titleId)).length;
        const percent = total ? Math.round(100 * owned / total) : 0;
        view.ring.style.setProperty("--value", String(percent));
        view.ring.setAttribute("aria-valuenow", String(percent));
        view.ring.setAttribute("aria-label", `${view.label}: ${text("owned_total", { owned, total })}`);
        content(view.percent, `${percent}%`);
        content(view.count, `${owned} / ${total}`);
        content(view.remaining, total ? t("fishydex.progress.remaining", { count: total - owned }) : text("empty_grade"));
      }
      if (totals) content(totals, catalog ? text("owned_total", {
        owned: index.ownedIds.size, total: [...groups.values()].reduce((sum, rows) => sum + rows.length, 0),
      }) : "");
    },
    dispose: clear,
  };
}
